import EventClass from '@/orm/models/EventClass'
import EventBoat from '@/orm/models/EventBoat'
import Event from '@/orm/models/Event'
import { Defaults, EventListOrder, Options } from '@/orm/models/Setting'
import SettingsRepo from '@/orm/repositories/SettingsRepo'
import { Repository, useRepo } from 'pinia-orm'

export default class EventsRepo extends Repository {
    use = Event

    selectEvent(id: string): void {
        const event = this.find(id) as Event

        if (event == null)
            return

        event.lastOpened = new Date()

        if (event.currentTab == null)
            event.currentTab = 'EventConfigurationTab'

        this.save(event)
    }

    getEvent(id: string, details: boolean = false): Event | null {
        EventBoat.clearEventClassCache()
        EventClass.clearCache()

        const event = details
            ? this.where('id', id)
                .with('races', (query) => {
                    query.with('event', (qe) => {
                        qe.with('classes', (qc) => {
                            qc.with('boatClass')
                        })
                        qe.with('boats', (qb) => {
                            qb.with('event')
                            qb.with('boatClass')
                            qb.with('eventBoatRaces')
                            qb.with('crew', (q) => {
                                q.with('eventBoat')
                                q.with('member', (qm) => {
                                    qm.with('club')
                                })
                                q.with('nonMemberCitizenshipCountry')
                            })
                            qb.with('manualCategories')
                        })
                        qe.with('directorMember')
                    })
                    query.with('results', (q) => {
                        q.with('race', (qr) => {
                            qr.with('results', (qrr) => {
                                qrr.with('boat', (qrb) => {
                                    qrb.with('event')
                                    qrb.with('boatClass')
                                })
                            })
                        })
                        q.with('boat', (qb) => {
                            qb.with('event')
                            qb.with('boatClass')
                            qb.with('crew', (qc) => {
                                qc.with('member')
                                qc.with('nonMemberCitizenshipCountry')
                            })
                        })
                    })
                    query.with('rounds', (q) => {
                        q.with('boat', (qb) => {
                            qb.with('boatClass')
                        })
                    })
                    query.with('skippedClasses', (q) => {
                        q.with('boatClass')
                    })
                    query.with('detailedClasses', (q) => {
                        q.with('boatClass')
                    })
                })
                .with('classes', (query) => {
                    query.with('event')
                    query.with('categories', (q) => {
                        q.with('eventClass')
                    })
                    query.with('boatClass')
                })
                .with('boats', (query) => {
                    query.with('event')
                    query.with('boatClass')
                    query.with('eventBoatRaces')
                    query.with('crew', (q) => {
                        q.with('eventBoat')
                        q.with('member', (qm) => {
                            qm.with('club')
                        })
                        q.with('nonMemberCitizenshipCountry')
                    })
                    query.with('manualCategories')
                })
                .with('referees', (query) => {
                    query.with('referee')
                    query.with('refereeRole')
                    query.with('course')
                })
                .with('lifeguards', (query) => {
                    query.with('lifeguard')
                    query.with('lifeguardRole')
                })
                .with('courses', (query) => {
                    query.with('objects')
                })
                .with('noticeboardEntries', (query) => {
                    query.with('files')
                })
                .with('club')
                .with('venue')
                .with('directorMember')
                .with('guarantorMember')
                .first()
            : this.where('id', id)
                .with('races', (query) => {
                    query.with('event', (qe) => {
                        qe.with('classes', (qc) => {
                            qc.with('boatClass')
                        })
                        qe.with('boats', (qb) => {
                            qb.with('event')
                            qb.with('boatClass')
                            qb.with('eventBoatRaces')
                            qb.with('crew', (q) => {
                                q.with('eventBoat')
                                q.with('member', (qm) => {
                                    qm.with('club')
                                })
                                q.with('nonMemberCitizenshipCountry')
                            })
                            qb.with('manualCategories')
                        })
                        qe.with('directorMember')
                    })
                    query.with('results', (q) => {
                        q.with('race', (qr) => {
                            qr.with('results', (qrr) => {
                                qrr.with('boat', (qrb) => {
                                    qrb.with('event')
                                    qrb.with('boatClass')
                                    qrb.with('crew', (qc) => {
                                        qc.with('member')
                                        qc.with('nonMemberCitizenshipCountry')
                                    })
                                })
                            })
                        })
                        q.with('boat', (qb) => {
                            qb.with('event')
                            qb.with('boatClass')
                            qb.with('crew', (qc) => {
                                qc.with('member')
                                qc.with('nonMemberCitizenshipCountry')
                            })
                        })
                    })
                    query.with('skippedClasses')
                })
                .with('classes', (query) => {
                    query.with('event')
                    query.with('boatClass')
                })
                .with('boats', (query) => {
                    query.with('event')
                    query.with('boatClass')
                })
                .with('directorMember')
                .with('guarantorMember')
                .first()

        if (event == null)
            return null

        return (event as Event)
    }

    eventByCtlId(ctlId: string) {
        return this.where('ctlId', ctlId).first()
    }

    orderedListAll() {
        const eventListOrder = parseInt(
            useRepo(SettingsRepo).getOption(Options.EventListOrder, Defaults.EventListOrder),
        )
        const field = eventListOrder === EventListOrder.Created
            ? 'created'
            : (eventListOrder === EventListOrder.LastOpened ? 'lastOpened' : 'from')

        return this.orderBy(event => new Date(event[field]).toISOString(), 'desc').get()
    }

    orderedListLimited() {
        const eventListOrder = parseInt(
            useRepo(SettingsRepo).getOption(Options.EventListOrder, Defaults.EventListOrder),
        )
        const raceShown = parseInt(useRepo(SettingsRepo).getOption(Options.RaceShown, Defaults.RaceShown))
        const field = eventListOrder === EventListOrder.Created
            ? 'created'
            : (eventListOrder === EventListOrder.LastOpened ? 'lastOpened' : 'from')

        return this.orderBy(event => new Date(event[field]).toISOString(), 'desc').limit(raceShown).get()
    }

    moreThanLimited() {
        const raceShown = useRepo(SettingsRepo).getOption(Options.RaceShown, Defaults.RaceShown)

        return this.all().length > raceShown
    }

    createEvent(event) {
        const storedEvent = this.save({
            created: new Date(),
            ...event,
            id: parseInt(useRepo(SettingsRepo).getOption(Options.SeqEvent, Defaults.SeqEvent)) + 1,
            lastOpened: new Date(),
        }) as Event

        useRepo(SettingsRepo).setOption(Options.SeqEvent, String(storedEvent.id))

        return storedEvent
    }
}
